import { motion } from 'framer-motion'
import { MessagesSquare, GitPullRequest, ShieldCheck, Activity } from 'lucide-react'
import { BentoGrid, BentoGridItem } from '@/components/animations/BentoGrid'

function RepositoryDemo() {
  return (
    <div className="flex flex-1 w-full min-h-[12rem] rounded-lg bg-surface border border-line p-4 flex-col gap-3 font-mono text-[10px] sm:text-xs overflow-hidden relative select-none">
      <motion.div initial={{ opacity: 0, y: 5 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-ink flex gap-2">
        <span className="text-primary">{'>'}</span> What changed this week?
      </motion.div>
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.6 }} className="pl-4 space-y-1">
        <div className="text-ok">✓ 42 commits analyzed</div>
        <div className="text-ok">✓ 7 pull requests</div>
        <div className="text-ok">✓ 3 issues</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 5 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="mt-2 text-ink-muted pl-4">
        <div className="text-ink font-semibold mb-1">Summary</div>
        Authentication module updated by 3 contributors...
      </motion.div>
    </div>
  )
}

function PullRequestDemo() {
  return (
    <div className="flex flex-1 w-full min-h-[12rem] rounded-lg bg-surface border border-line p-4 flex-col gap-2 font-mono text-[10px] overflow-hidden relative select-none">
      <div className="text-ink-muted">Pull Request #284</div>
      <div className="text-ok/80 bg-ok/10 px-2 py-1 rounded-sm border border-ok/20">+ auth/service.ts</div>
      
      <motion.div initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="mt-2 bg-waiting/10 border border-waiting/20 rounded p-2">
        <div className="text-waiting font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Potential issue</div>
        <div className="text-ink-muted mt-1">Token validation may fail under load.</div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8 }} className="mt-auto">
        <div className="bg-raised border border-line rounded px-2 py-1 text-center text-primary cursor-pointer hover:bg-hover">
          [ Apply suggestion ]
        </div>
      </motion.div>
    </div>
  )
}

function ApprovalDemo() {
  return (
    <div className="flex flex-1 w-full min-h-[12rem] rounded-lg bg-surface border border-line p-4 flex-col font-mono text-[10px] overflow-hidden relative justify-center select-none">
      <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 2 }} className="text-ink-muted text-center mb-4">
        AI wants to execute:
      </motion.div>
      <div className="bg-ground border border-line rounded p-2 text-center text-ink mb-4">
        github.create_issue
      </div>
      
      <div className="grid grid-cols-2 gap-2 mt-auto">
        <motion.div whileHover={{ scale: 1.05 }} className="bg-surface border border-line rounded py-1.5 text-center text-ink-muted cursor-pointer hover:bg-hover">
          Reject
        </motion.div>
        <motion.div whileHover={{ scale: 1.05 }} className="bg-ok/20 border border-ok/30 rounded py-1.5 text-center text-ok cursor-pointer hover:bg-ok/30">
          Approve
        </motion.div>
      </div>
    </div>
  )
}

function AuditDemo() {
  return (
    <div className="flex flex-1 w-full min-h-[12rem] rounded-lg bg-surface border border-line p-4 flex-col gap-2 font-mono text-[10px] overflow-hidden relative select-none">
      <div className="flex justify-between items-center text-ink-muted mb-2 border-b border-line pb-2">
        <span>TOOL CALL</span>
        <span>github.get_pull_request</span>
      </div>
      
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.2 }} className="grid grid-cols-3 gap-2">
        <div className="text-ink-muted">repo:</div>
        <div className="col-span-2 text-ink">acme/backend</div>
        
        <div className="text-ink-muted">pr:</div>
        <div className="col-span-2 text-ink">#284</div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-auto grid grid-cols-3 gap-2 border-t border-line pt-2">
        <div className="text-ink-muted">Result</div>
        <div className="col-span-2 text-ok">200 OK</div>
        
        <div className="text-ink-muted">Latency</div>
        <div className="col-span-2 text-ink">182ms</div>
        
        <div className="text-ink-muted">Credits</div>
        <div className="col-span-2 text-ink">0.42</div>
      </motion.div>
    </div>
  )
}

export function CapabilitiesBento() {
  const features = [
    {
      icon: MessagesSquare,
      title: 'Ask about your repositories',
      body: 'Open issues, recent pull requests, what changed and who changed it — in a conversation that remembers what you already asked.',
      className: 'md:col-span-2',
      demo: <RepositoryDemo />
    },
    {
      icon: GitPullRequest,
      title: 'Review a pull request',
      body: 'The assistant reads the diff, finds real problems, and posts a review with suggested corrections you can apply in one click on GitHub.',
      className: 'md:col-span-1',
      demo: <PullRequestDemo />
    },
    {
      icon: ShieldCheck,
      title: 'Nothing changes without you',
      body: 'Every write — an issue, a label, a review — stops for your approval first, and a read-only mode refuses them entirely.',
      className: 'md:col-span-1',
      demo: <ApprovalDemo />
    },
    {
      icon: Activity,
      title: 'Every call is on the record',
      body: 'Each tool call is stored with its arguments, outcome and latency, so you can see exactly what the assistant did on your behalf.',
      className: 'md:col-span-2',
      demo: <AuditDemo />
    },
  ]

  return (
    <section className="py-24 sm:py-32 bg-ground relative">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold text-ink tracking-tight">
            Everything you need. Nothing you don't.
          </h2>
          <p className="mt-4 text-lg text-ink-muted">
            Powerful DevOps workflows built around security and observability.
          </p>
        </div>
        
        <BentoGrid>
          {features.map((item) => (
            <BentoGridItem
              key={item.title}
              title={item.title}
              description={item.body}
              className={item.className}
              icon={<item.icon className="h-6 w-6 text-primary" />}
              header={item.demo}
            />
          ))}
        </BentoGrid>
      </div>
    </section>
  )
}
